import { FC, useState, useEffect, useRef } from 'react';
import Activity from './Activity';
import Gamification, { Activity as ActivityType } from '../../services/Gamification';

import styled from 'styled-components';

const PAGE_SIZE = 5;

const Container = styled.section<{ scrollable: boolean }>(({ scrollable }) => {
  return `
    height: ${scrollable ? '100%' : 'auto'};
  `;
});

const Heading = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    font-family: ${type.face};
    font-size: ${type.size.regular};
    font-weight: ${type.weight.bold};
    line-height: 1.1875rem;
    text-transform: uppercase;
    margin: 0 0 21px;
    flex-grow: 1;
    grid-column: 1;
    grid-row: 1;
  `;
});

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
`;

const ViewAll = styled.button(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-family: ${type.face};
    font-weight: ${type.weight.bold};
    font-size: ${type.size.tiny};
    grid-column: 2;
    grid-row: 1;
    border: 0;
    background-color: transparent;
    margin: 0 0 21px;
    cursor: pointer;
    display: inline-block;
    flex-shrink: 1;
    &:hover {
      text-decoration: underline;
    }
  `;
});

const ActivityList = styled.div<{ scrollable: boolean }>(({ scrollable }) => {
  return `
    overflow-y: ${scrollable ? 'scroll' : 'visible'};
    grid-row: 2;
    grid-column: 1 / span 2;
    max-height: 600px;
  `;
});

const ActivityTable = styled.table`
  border-collapse: separate;
  border-spacing: 0 4px;
  width: 100%;
`;

const LoadMoreButton = styled.button(({ theme }) => {
  const { type } = theme;

  return `
    font-family: ${type.face};
    font-weight: ${type.weight.bold};
    font-size: ${type.size.tiny};
  `;
});

const FilterButtonContainer = styled.div`
  margin-top: 10px;
`;

const FilterButton = styled.button(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-family: ${type.face};
    font-weight: ${type.weight.bold};
    font-size: ${type.size.tiny};
    padding: 5px;
    border-radius: 5px;
    cursor: pointer;
    margin-left: 10px;

    &:hover {
      text-decoration: underline;
    }

    &.active {
      background: ${color.primary.regular};
      color: white;
    }
  `;
});

const Loading = styled.div(({ theme }) => {
  const { type } = theme;
  return `
    font-size: ${type.size.small};
  `;
});

export interface IActivityFeed {
  viewAllAction?: () => void;
}

const AchievementActivityFeed: FC<IActivityFeed> = ({ viewAllAction }) => {
  let maxItems = 5;
  let fullView = false;

  if (typeof viewAllAction === 'undefined') {
    maxItems = -1;
    fullView = true;
  }

  const listRef = useRef<HTMLDivElement>(null);
  const limited = maxItems !== -1;
  const [activityInfo, setActivityInfo] = useState<ActivityType[]>([]);
  const [showLoadMore, setShowLoadMore] = useState<boolean>(false);
  const [userOnly, setUserOnly] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);

  const activityArray = limited ? activityInfo.slice(0, maxItems) : activityInfo;

  const activityLoop = activityArray.map(({ text, badge, highlight, name, titleName }, index) => {
    return (
      <Activity
        text={text}
        badge={badge}
        highlight={highlight}
        name={name}
        key={`activity-${index}`}
        titleName={titleName}
      />
    );
  });

  const getActivities = async (page: number, userOnly: boolean) => {
    setShowLoadMore(false);
    setLoading(true);

    const activities = await Gamification.getActivityFeed(page, userOnly);

    if (activities.length >= PAGE_SIZE) {
      setShowLoadMore(true);
    }

    if (page === 0) {
      setActivityInfo(activities);
    } else {
      setActivityInfo([...activityInfo, ...activities]);
    }

    if (listRef && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }

    setLoading(false);
  };

  const updateFilter = async (userOnly: boolean) => {
    await setActivityInfo([]);
    await setUserOnly(userOnly);
    await setPage(0);
    getActivities(0, userOnly);
  };

  const loadMore = () => {
    setPage(page + 1);
    getActivities(page + 1, userOnly);
  };

  useEffect(() => {
    getActivities(page, userOnly);
  }, []);

  const UserOnlyClass = userOnly ? 'active' : '';
  const AllClass = userOnly ? '' : 'active';

  return (
    <Container scrollable={!limited} className="gamification_tour_2">
      <HeaderContainer>
        <Heading>Activity Feed</Heading>

        {!fullView && (
          <ViewAll type="button" onClick={viewAllAction}>
            View all
          </ViewAll>
        )}

        {fullView && (
          <FilterButtonContainer>
            <FilterButton
              className={UserOnlyClass}
              onClick={() => {
                updateFilter(true);
              }}
            >
              Me
            </FilterButton>
            <FilterButton
              className={AllClass}
              onClick={() => {
                updateFilter(false);
              }}
            >
              All
            </FilterButton>
          </FilterButtonContainer>
        )}
      </HeaderContainer>

      <ActivityList scrollable={!limited} ref={listRef}>
        <ActivityTable>
          <tbody>{activityLoop}</tbody>
        </ActivityTable>

        {fullView && showLoadMore && <LoadMoreButton onClick={loadMore}>Load More</LoadMoreButton>}

        {loading && <Loading>Loading...</Loading>}
      </ActivityList>
    </Container>
  );
};

export default AchievementActivityFeed;
